/**
 * The chapter list — the view, shared by BuzzBrain and zitmus.
 *
 * Pure: it is handed a `ChapterListViewModel` and draws it. No fetching, no
 * router, no filtering, no knowledge of which product is asking. Everything
 * that differs between the two lives in the view model each builds, which is
 * why this can be the same file in an app and on a static site.
 *
 * zitmus renders it WITHOUT a `client:*` directive, so Astro emits the markup at
 * build time and ships no JavaScript for it: the page stays static and
 * crawlable, and zitmus's own filter script keeps reading `[data-chapter]` and
 * `[data-item]` off the DOM exactly as it does today. That is why the view
 * passes `dataAttributes` through untouched, and why an entry with an `href`
 * renders a real anchor rather than a click handler.
 *
 * Type and colour are NOT shared — see `ChapterList.css` for the contract each
 * product fills from its own tokens.
 */
import type { ChapterEntry, ChapterListViewModel } from './types';
import { LabMark } from '../lab-marks';
import './ChapterList.css';

function Entry({ entry }: { entry: ChapterEntry }) {
    const t = entry.thumbnail;
    const inner = (
        <>
            <span className="chapter-entry-thumb" aria-hidden="true">
                {t?.kind === 'image' && <img src={t.url} alt="" loading="lazy" decoding="async" />}
                {t?.kind === 'glyph' && <span className="chapter-entry-glyph">{t.glyph}</span>}
                {t?.kind === 'lab' && <span className="chapter-entry-glyph"><LabMark lab={t.lab} size={40} /></span>}
                {t?.kind === 'text' && (
                    <span
                        className="chapter-entry-text"
                        style={t.accent ? { ['--chapter-entry-accent' as string]: t.accent } : undefined}
                    >
                        {t.text}
                    </span>
                )}
            </span>
            <span className="chapter-entry-name">
                {entry.title}
                {entry.kindLabel && <em className="chapter-entry-kind">{entry.kindLabel}</em>}
            </span>
        </>
    );

    // An anchor where there is somewhere to go, a button where there is
    // something to do. Not a styling choice: the static site needs a real link
    // and the app needs its router, and one element cannot be both honestly.
    return entry.href
        ? <a className="chapter-entry" href={entry.href} title={entry.title} data-item {...entry.dataAttributes}>{inner}</a>
        : <button type="button" className="chapter-entry" onClick={entry.onOpen} title={entry.title} data-item {...entry.dataAttributes}>{inner}</button>;
}

export function ChapterListView({ chapters }: ChapterListViewModel) {
    return (
        <div className="chapter-list">
            {chapters.map(c => (
                <section
                    key={c.id}
                    // One entry is mostly empty space at full width, so a
                    // single-entry chapter takes one column and the grid's own
                    // auto-placement pairs two of them. More spans both.
                    className={`chapter-card${c.entries.length > 1 ? ' chapter-card--wide' : ''}`}
                    style={{ ['--chapter-list-cap' as string]: `var(--subject-${c.subject}, var(--chapter-list-rule))` }}
                    data-chapter
                    {...c.dataAttributes}
                >
                    <header className="chapter-card-head">
                        <h2 className="chapter-card-name">{c.name}</h2>
                        <span className="chapter-card-meta">{c.meta}</span>
                    </header>
                    <div className="chapter-card-entries">
                        {c.entries.map(e => <Entry key={e.id} entry={e} />)}
                    </div>
                </section>
            ))}
        </div>
    );
}
